import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { StakeName } from '@/features/lobby/constants/filter-options';
import { TableFilter } from '@/services/lobby/lobbyServicesTypes';
import { GameValue } from '@/features/lobby/filters/filters';
import { BettingStructure } from '@/services/common/types/bettingStructure';
import { ReactComponent as SettingsIcon } from '@/assets/icons/settings.svg';
import Filters from './Filters';
import { PokerCategory } from '@/services/common/types/pokerCategory';
import ActiveFiltersBadge from './ActiveFiltersBadge';
import Button, { ButtonSizes, ButtonVariants } from '@/components/new-design/Button';

interface MobileFiltersProps {
  filters: TableFilter;
  onRangeChange: (minValue: number, maxValue: number) => void;
  onTableSizeChange: (size: number) => void;
  onEmptyTables?: () => void;
  onFullTables?: () => void;
  onStakes?: (gameStake: StakeName) => void;
  onGameType?: (gameType: BettingStructure) => void;
  onGames?: (game: GameValue) => void;
  onResetFilters: () => void;
  onSaveFilters: () => void;
  onCloseWithoutSaving: () => void;
  onCategoryChange?: (option?: PokerCategory) => void;
  children?: React.ReactNode;
}

const MobileFilters: React.FC<MobileFiltersProps> = ({
  filters,
  onTableSizeChange,
  onEmptyTables = () => null,
  onFullTables = () => null,
  onStakes = () => null,
  onGameType = () => null,
  onResetFilters = () => null,
  onSaveFilters = () => null,
  onCloseWithoutSaving = () => null,
  onCategoryChange,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  let numberOfFilters = 0;
  if (filters.games?.length) numberOfFilters++;
  if (filters.maxPlayers?.length) numberOfFilters++;
  if (filters.stakeOptions?.length) numberOfFilters++;
  if (filters.gameTypes?.length) numberOfFilters++;
  if (filters.fullTables) numberOfFilters++;
  if (filters.emptyTables) numberOfFilters++;
  if (filters.category) numberOfFilters++;

  const toggleFilters = () => {
    if (isOpen) {
      onCloseWithoutSaving();
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div className="flex w-full items-center justify-end gap-2">
        {children}
        <button
          className="peer relative flex h-11 w-11 items-center justify-center rounded-lg border border-ph-white-10 p-2.5 font-bold text-ph-white-50 data-[open=true]:!border-ph-white-30 data-[open=true]:!text-white"
          onClick={toggleFilters}
          data-open={isOpen}
        >
          <SettingsIcon className="h-6 w-6" />
          {!!numberOfFilters && (
            <ActiveFiltersBadge
              numberOfFilters={numberOfFilters}
              className="peer absolute right-0 top-0 -translate-y-1/3 translate-x-1/3"
            />
          )}
        </button>
      </div>

      <div
        className={twMerge(
          'flex flex-col overflow-hidden transition-all duration-500 ease-in-out',
          // the max-h is calculated by subtracting all the top element heights
          isOpen ? 'max-h-[calc(100vh-64px-88px-32px-48px)] pt-5' : 'max-h-0',
        )}
        data-open={isOpen}
      >
        <div className="scrollbar-phenom overflow-x-none flex w-full overflow-y-auto">
          <Filters
            filters={filters}
            onTableSizeChange={onTableSizeChange}
            onEmptyTables={onEmptyTables}
            onFullTables={onFullTables}
            onStakes={onStakes}
            onGameType={onGameType}
            onResetFilters={onResetFilters}
            onCategoryChange={onCategoryChange}
            isMobileView
            hideButtons
          />
        </div>
        <div className={twMerge('flex flex-1 justify-end gap-5', 'bg-ph-gray-darker bg flex flex-col p-4')}>
          <Button
            type="button"
            variant={ButtonVariants.transparent}
            className="whitespace-nowrap p-2"
            size={ButtonSizes.sm}
            onClick={onResetFilters}
          >
            Reset Filters
          </Button>

          <div className={twMerge('flex w-full items-center justify-end gap-5', 'justify-center')}>
            <Button
              type="button"
              className={twMerge('bg-pn-violet-medium', 'w-1/2')}
              variant={ButtonVariants.secondary}
              onClick={() => {
                onCloseWithoutSaving();
                setIsOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className={twMerge('w-1/2')}
              onClick={() => {
                onSaveFilters();
                setIsOpen(false);
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileFilters;
